/**
 * Video detail page — verdict-first layout.
 *
 * The old layout buried the "is this video safe?" question under a wall of
 * agent cards. This rewrite puts a big verdict tile next to the player,
 * follows it with a sortable agent scorecard ribbon (harmful → neutral →
 * beneficial so concerns are never below the fold), then the detailed
 * agent cards as before. The description and admin-only tools move to
 * collapsed / drawer affordances so they don't compete with the verdict.
 *
 * The feedback entry point is now a single primary button that opens the
 * redesigned right-side FeedbackDrawer (was FeedbackDialog). Per-agent
 * Agree/Disagree buttons on each card now open the same drawer focused on
 * that agent — one mental model, one flow.
 */
import React, {useEffect, useMemo, useState} from 'react';
import {ApolloClient, NormalizedCacheObject} from '@apollo/client';
import {useParams, Link as RouterLink} from 'react-router-dom';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Alert,
  Avatar,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControl,
  IconButton,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  SelectChangeEvent,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import ReportGmailerrorredIcon from '@mui/icons-material/ReportGmailerrorred';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import RemoveCircleOutlineIcon from '@mui/icons-material/RemoveCircleOutline';
import BlockIcon from '@mui/icons-material/Block';
import BugReportIcon from '@mui/icons-material/BugReport';
import ScienceIcon from '@mui/icons-material/Science';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import {
  AgentAggregateScore,
  AgentScore,
  ScoreDirection,
  Video,
  VideoRating,
  AgentAnalysisChatLog,
  AgentSpecialization,
  AdminScoringTestResult,
  AgreementType,
} from '../../types/generated/graphql';
import {
  useGetScoresForVideo,
  useGetAllFeedbackForVideo,
  useGetAgentAnalysisChatLog,
  useAdminTestAgentScoring,
} from '../../api/graphql_agent_scores';
import {getAggregateScoreLoader} from '../../api/graphql_video_aggregate_score';
import PendingActionBanner from './PendingActionBanner';
import {createCachedVideosLoader} from '../../api/graphql_video_cache';
import {onAuthStateChanged} from 'firebase/auth';
import {auth} from '../../firebase';
import {getScoreColor, COLORS, latestScoresByAgent} from '../../utils/scoring';
import {pcolors, cardSx, sectionLabelSx} from '../../theme/planapusTheme';
import {humanizeSpecialization, humanizeVideoRating} from '../Admin/agentLabels';
import {useListEvalAgents} from '../../api/graphql_evals';
import {
  useAdminResetVideoScores,
  useAdminResetVideoSummary,
  useRescoreVideosAsync,
} from '../../api/graphql_admin_rescore';
import {ScoringChainPanel} from './ScoringChainPanel';
import {TranscriptPanel} from './TranscriptPanel';
import {EvalLabPanel} from './EvalLabPanel';
import {LanguageChip} from './LanguageChip';
import {useGetVideoLanguage} from '../../api/graphql_video_transcript';
import {useConfirm, useNotify} from '../ui/ConfirmDialog';

interface Props {
  client: ApolloClient<NormalizedCacheObject>;
}

// ---------------------------------------------------------------------------
// Small local helpers
// ---------------------------------------------------------------------------

function ratingIcon(rating: VideoRating, size = 18) {
  switch (rating) {
    case VideoRating.Beneficial:
      return <CheckCircleIcon sx={{fontSize: size, color: COLORS.GREEN}} />;
    case VideoRating.Neutral:
      return <RemoveCircleOutlineIcon sx={{fontSize: size, color: COLORS.GRAY}} />;
    case VideoRating.Harmful:
      return <BlockIcon sx={{fontSize: size, color: COLORS.RED}} />;
    default:
      return null;
  }
}

/**
 * Stable sort key — alphabetical by specialization so a given agent
 * appears in the same row across re-evals and across side-by-side
 * comparison columns in the Eval Lab. Previously sorted by concern
 * rank (harmful-first), but that made side-by-side diffs noisy: the
 * same agent could jump rows depending on the model's verdict.
 */
function specializationKey(score: AgentScore): string {
  return String(score.agent.specialization);
}

// ---------------------------------------------------------------------------

export default function OneVideo({client}: Props) {
  const {pid, vid} = useParams();

  // Eager but lightweight: the language-only query hits the unified
  // summary doc and returns a small object — cheap to fetch alongside
  // the initial page load so the chip is visible without requiring
  // the admin to expand the transcript panel first.
  const {data: languageData} = useGetVideoLanguage(client, vid || '');
  const language = languageData?.getVideoLanguage ?? null;

  // When an admin fires a Re-eval we flip `rescoreWatchUntil` to a timestamp
  // ~3 minutes out; while that's in the future the query polls every 4s so
  // the chain-visualization repopulates when the background job finishes.
  // Once we see a fresh triage row we stop polling immediately (below).
  const [rescoreWatchUntil, setRescoreWatchUntil] = useState(0);
  const pollActive = Date.now() < rescoreWatchUntil;
  const {
    data: scoresData,
    loading: scoresLoading,
    error: scoresError,
    refetch: refetchScores,
  } = useGetScoresForVideo(client, vid || '', pollActive ? 4000 : 0);
  // Age-band filter — mirrors the one on /admin/content/videos. When a band
  // is selected the aggregate loader fetches with `childAge` so the
  // VerdictTile shows the finalize row's per-age-band verdict (score
  // + rating) instead of the universal family-agnostic aggregate.
  // `null` = universal; band values are midpoint ages for pickAgeBand.
  const [selectedChildAge, setSelectedChildAge] = useState<number | null>(null);
  const aggregateScoreLoader = getAggregateScoreLoader(client, selectedChildAge);
  const videoLoader = createCachedVideosLoader(client);

  const [aggregateScore, setAggregateScore] = useState<AgentAggregateScore | null>(null);
  // Distinguishes "still fetching" from "fetched, no score exists yet" so
  // the VerdictTile can render a terminal empty state instead of a perma-
  // spinner for videos that simply haven't been scored.
  const [aggregateLoading, setAggregateLoading] = useState(true);
  const [videoData, setVideoData] = useState<Video | null>(null);
  const [isAdmin, setIsAdmin] = useState(false);

  // Feedback drawer state — the entry point from the hero CTA and from
  // per-card agree/disagree.

  // Admin dialogs — dialogs for now (future: drawer) to keep behaviour.
  const [logScore, setLogScore] = useState<AgentScore | null>(null);
  const [chatLog, setChatLog] = useState<AgentAnalysisChatLog | null>(null);
  const [testScore, setTestScore] = useState<AgentScore | null>(null);
  const [identityOverride, setIdentityOverride] = useState('');
  const [extraGuidelines, setExtraGuidelines] = useState('');
  const [testResult, setTestResult] = useState<AdminScoringTestResult | null>(null);

  const {
    getAgentAnalysisChatLog,
    data: chatLogData,
    loading: chatLogLoading,
    error: chatLogError,
  } = useGetAgentAnalysisChatLog(client);
  const {adminTestAgentScoring, loading: testLoading, error: testError} = useAdminTestAgentScoring(
    client,
  );

  // `getScoresForVideo` returns full score history including superseded
  // runs. Collapse to latest-per-agent *once* here so the verdict summary,
  // ribbon, and agent cards all agree. See `latestScoresByAgent` in
  // utils/scoring for the de-dup rule.
  const agentScores = useMemo(
    () => latestScoresByAgent(scoresData?.getScoresForVideo ?? []),
    [scoresData?.getScoresForVideo],
  );

  // Stop polling once the Re-eval job has written a fresh triage row —
  // the chain-visualization now has everything it needs. This avoids
  // polling for the full 3-minute ceiling when the job finishes early.
  useEffect(() => {
    if (!pollActive) return;
    const hasFreshTriage = agentScores.some(s => s.gatedContext?.stage === 0);
    if (hasFreshTriage) setRescoreWatchUntil(0);
  }, [agentScores, pollActive]);

  // Full agent registry — feeds the ScoringChainPanel's "skipped" list so
  // we can render specialists that were applicable but didn't run.
  const {data: agentsData} = useListEvalAgents(client);
  const allAgents = agentsData?.listEvalAgents ?? [];

  // Every feedback row attached to this video (not user-scoped). Passed
  // to the chain panel so a feedback-override is rendered as a
  // first-class specialist row, paired with the agent row it overrode
  // (if any).
  const {data: feedbackData} = useGetAllFeedbackForVideo(client, vid || '');
  const allFeedback = feedbackData?.getAllFeedbackForVideo ?? [];

  const [resetScores, {loading: resetting}] = useAdminResetVideoScores(client);
  const [rescoreVideos, {loading: rescoring}] = useRescoreVideosAsync(client);
  const [resetSummary, {loading: resettingSummary}] = useAdminResetVideoSummary(client);
  const confirm = useConfirm();
  const notify = useNotify();

  // Per-video summary nuke. Deletes `video_summaries` rows for this
  // specific video so the next scoring pass regenerates the summary
  // with the current summarizer code (fresh provenance header,
  // language detection, etc.). Does not touch agent scores — those
  // still reference their cache_llm row and continue to render; the
  // new summary only matters on the next re-eval.
  const handleResetSummary = async () => {
    if (!vid) return;
    const ok = await confirm({
      title: 'Reset summary for this video?',
      message:
        `Delete cached summaries (both unified + detailed tiers) for video ${vid}. ` +
        'The next scoring pass will regenerate the summary from scratch with the ' +
        'current summarizer code (new [DATA: ...] provenance header + language ' +
        'detection). Existing agent scores are NOT touched.',
      confirmLabel: 'Reset summary',
      destructive: true,
    });
    if (!ok) return;
    try {
      const res = await resetSummary({variables: {videoId: vid}});
      const n = res.data?.adminResetVideoSummary?.summariesDeleted ?? 0;
      notify({
        severity: 'success',
        message: n
          ? `Deleted ${n} summary row${n === 1 ? '' : 's'}. Re-eval the video to regenerate.`
          : 'No cached summaries to delete — the video has never been summarized.',
      });
    } catch (err) {
      notify({
        severity: 'error',
        message: `Summary reset failed: ${err instanceof Error ? err.message : String(err)}`,
      });
    }
  };

  const handleResetScores = async () => {
    if (!vid) return;
    const ok = await confirm({
      title: 'Reset agent scores?',
      message: `Delete ALL agent scores for video ${vid}? This clears the chain-visualization and forces a fresh scoring run next time the video is rescored.`,
      confirmLabel: 'Reset',
      destructive: true,
    });
    if (!ok) return;
    try {
      const res = await resetScores({variables: {videoIds: [vid]}});
      const n = res.data?.adminResetVideoScores?.scoresDeleted ?? 0;
      // Clear the in-memory query so the chain panel reflects the wipe
      // immediately — refetchQueries on the mutation is a no-op with
      // fetchPolicy: 'network-only', so we trigger it explicitly.
      await refetchScores();
      notify({
        severity: 'success',
        message: `Deleted ${n} score row${n === 1 ? '' : 's'}. Rescore from the admin panel to repopulate.`,
      });
    } catch (err) {
      notify({
        severity: 'error',
        message: `Reset failed: ${err instanceof Error ? err.message : String(err)}`,
      });
    }
  };

  // One-click "Re-eval" — queues a BulkRescore BullMQ job scoped to just
  // this video. Always `resetFirst: true` so the chain-visualization
  // starts clean. Progress + logs surface on the Admin → Jobs page.
  const handleReeval = async () => {
    if (!vid) return;
    const ok = await confirm({
      title: 'Re-eval this video?',
      message: `Delete every existing score for video ${vid} and run a fresh scoring pass through the gated pipeline. Follow progress on Admin → Jobs.`,
      confirmLabel: 'Re-eval',
      destructive: true,
    });
    if (!ok) return;
    try {
      const res = await rescoreVideos({
        variables: {
          input: {
            // sampleSize is ignored when videoIds is provided — keep a
            // sane value so schema validation passes.
            sampleSize: 1,
            videoIds: [vid],
            resetFirst: true,
            skipExisting: false,
            agents: null,
          },
        },
      });
      // Start polling getScoresForVideo so the chain-visualization
      // updates in place when the background job finishes.
      setRescoreWatchUntil(Date.now() + 3 * 60 * 1000);
      const jobId = res.data?.rescoreVideosAsync?.jobId;
      notify({
        severity: 'success',
        message: jobId
          ? `Re-eval queued as job #${jobId}.`
          : 'Re-eval queued. Check Admin → Jobs for progress; the chain-visualization will update when scoring completes.',
        action: jobId ? {label: 'View job', href: `#/admin/operations/tasks?jobId=${jobId}`} : undefined,
        durationMs: 8000,
      });
    } catch (err) {
      notify({
        severity: 'error',
        message: `Re-eval failed: ${err instanceof Error ? err.message : String(err)}`,
      });
    }
  };
  const sortedScores = useMemo(
    () =>
      [...agentScores].sort((a, b) =>
        specializationKey(a).localeCompare(specializationKey(b)),
      ),
    [agentScores],
  );

  // Admin check ------------------------------------------------------------
  useEffect(() => {
    const unsub = onAuthStateChanged(auth, async user => {
      if (!user) {
        setIsAdmin(false);
        return;
      }
      try {
        const t = await user.getIdTokenResult();
        setIsAdmin(t.claims.role === 'admin');
      } catch {
        setIsAdmin(false);
      }
    });
    return () => unsub();
  }, []);

  // Hydrate video + aggregate ---------------------------------------------
  //
  // `aggregateScoreLoader` is a new instance whenever `selectedChildAge`
  // changes (the loader cache in `getAggregateScoreLoader` keys on
  // (client, childAge)) — list it in the deps so flipping the age
  // selector triggers a refetch with the band-scoped query.
  useEffect(() => {
    if (!vid) return;
    let mounted = true;
    setAggregateLoading(true);
    // Clear the current aggregate immediately so the VerdictTile
    // doesn't flash the previous band's rating during the swap.
    setAggregateScore(null);
    aggregateScoreLoader
      .load(vid)
      .then(d => {
        if (!mounted) return;
        setAggregateScore(d);
        setAggregateLoading(false);
      })
      .catch(err => {
        console.error('aggregate score load failed:', err);
        if (mounted) setAggregateLoading(false);
      });
    videoLoader
      .load(vid)
      .then(d => mounted && setVideoData(d))
      .catch(err => console.error('video load failed:', err));
    return () => {
      mounted = false;
    };
  }, [vid, aggregateScoreLoader]);

  useEffect(() => {
    if (chatLogData?.getAgentAnalysisChatLog) {
      setChatLog(chatLogData.getAgentAnalysisChatLog);
    }
  }, [chatLogData]);

  // Admin actions ----------------------------------------------------------
  const openLog = async (s: AgentScore) => {
    setLogScore(s);
    setChatLog(null);
    if (isAdmin && s.cache_llm_id) {
      await getAgentAnalysisChatLog({variables: {cache_llm_id: s.cache_llm_id}}).catch(console.error);
    }
  };
  const openTest = (s: AgentScore) => {
    setTestScore(s);
    setTestResult(null);
    setIdentityOverride('');
    setExtraGuidelines('');
  };
  const runTest = async () => {
    if (!vid || !testScore?.agent) return;
    const res = await adminTestAgentScoring({
      variables: {
        videoId: vid,
        agent: {
          specialization: testScore.agent.specialization,
          version: testScore.agent.version,
        },
        overrides:
          identityOverride || extraGuidelines
            ? {
                identity: identityOverride || undefined,
                extraGuidelines: extraGuidelines || undefined,
              }
            : null,
      },
    });
    if (res.data?.adminTestAgentScoring) {
      setTestResult(res.data.adminTestAgentScoring);
    }
  };

  // ------------------------------------------------------------------ render
  return (
    <Box sx={{maxWidth: 1280, mx: 'auto', p: {xs: 2, sm: 3}}}>
      {pid && vid && (
        <PendingActionBanner client={client} profileId={pid} videoId={vid} />
      )}
      {scoresLoading && !videoData && (
        <Box sx={{display: 'flex', justifyContent: 'center', py: 6}}>
          <CircularProgress size={28} />
        </Box>
      )}
      {scoresError && (
        <Alert severity="error" sx={{mb: 3}}>
          Error loading agent scores: {scoresError.message}
        </Alert>
      )}

      {vid && (
        <>
          {/* =============================================================
              HERO: video player + verdict tile
              ============================================================= */}
          <Box sx={{display: 'grid', gridTemplateColumns: {xs: '1fr', lg: '1fr 360px'}, gap: 3, mb: 3}}>
            <Box>
              <Box
                sx={{
                  position: 'relative',
                  paddingBottom: '56.25%',
                  height: 0,
                  overflow: 'hidden',
                  borderRadius: '12px',
                  bgcolor: '#000',
                }}
              >
                <iframe
                  style={{position: 'absolute', top: 0, left: 0, width: '100%', height: '100%', border: 0, borderRadius: 12}}
                  src={`https://www.youtube.com/embed/${vid}`}
                  title={videoData?.title || `Video ${vid}`}
                  allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                  allowFullScreen
                />
              </Box>
              {/* Title + channel — tight, under the player. Admin
                  actions used to live on this row; moved down to their
                  own row just above the scoring-chain panel so the
                  header stays focused on the *video* (title, channel,
                  detected language) and actions cluster near the
                  inspection surface they apply to. */}
              <Box sx={{mt: 2}}>
                {/* Title + language chip on the same line. Chip is
                    right-aligned via `ml: auto` on the flex row so it
                    hugs the right edge even when the title is short. */}
                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 1.5,
                    minWidth: 0,
                  }}
                >
                  <Typography
                    sx={{
                      fontSize: 20,
                      fontWeight: 700,
                      color: pcolors.textPrimary,
                      lineHeight: 1.3,
                      flex: 1,
                      minWidth: 0,
                    }}
                  >
                    {videoData ? videoData.title : `Video ${vid}`}
                  </Typography>
                  <Box sx={{flex: 'none'}}>
                    <LanguageChip language={language} />
                  </Box>
                </Box>
                {videoData?.author && (
                  <Box
                    // Channel nav only makes sense in a profile context.
                    // On the admin-scoped route (`/admin/content/video/:vid`) there's
                    // no per-profile channel page to jump to, so we render
                    // the author as plain text instead of a link.
                    {...(pid
                      ? {
                          component: RouterLink,
                          to: `/profile/${pid}/channel/${videoData.author.id}`,
                        }
                      : {})}
                    sx={{
                      mt: 0.75,
                      display: 'inline-flex',
                      alignItems: 'center',
                      gap: 1,
                      color: pcolors.textSecondary,
                      textDecoration: 'none',
                      ...(pid
                        ? {
                            '&:hover': {
                              color: pcolors.textPrimary,
                              textDecoration: 'underline',
                            },
                          }
                        : {}),
                    }}
                  >
                    {videoData.author.thumbnails?.[0]?.url && (
                      <Avatar src={videoData.author.thumbnails[0].url} sx={{width: 22, height: 22}} />
                    )}
                    <Typography sx={{fontSize: 13, color: 'inherit'}}>{videoData.author.name}</Typography>
                  </Box>
                )}
              </Box>
            </Box>

            <VerdictTile
              aggregate={aggregateScore}
              agentScores={agentScores}
              loading={aggregateLoading}
            />
          </Box>

          {/* =============================================================
              CONTROL ROW — age-band filter + admin actions. Sits just
              above the scoring-chain panel so changing the age filter
              is visually close to the verdict + chain it re-scopes.
              Age selector renders for all viewers (the band verdicts
              are useful to parents choosing for a kid of a specific
              age, not just admins); admin actions render only when
              the user is an admin.
              ============================================================= */}
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'flex-end',
              gap: 1,
              mb: 1.5,
              flexWrap: 'wrap',
            }}
          >
            {/* Age filter. Values are band midpoint ages; the backend's
                pickAgeBandVerdict picks the band containing the age.
                `All ages` returns the universal family-agnostic
                verdict; band selections return the finalize row's
                per-age-band verdict for score + rating. */}
            <FormControl size="small" sx={{minWidth: 180}}>
              <InputLabel>Age</InputLabel>
              <Select
                value={selectedChildAge == null ? 'all' : String(selectedChildAge)}
                label="Age"
                onChange={(e: SelectChangeEvent) => {
                  const v = e.target.value;
                  setSelectedChildAge(v === 'all' ? null : Number(v));
                }}
              >
                <MenuItem value="all" sx={{fontSize: 13}}>All ages (universal)</MenuItem>
                <MenuItem value="3" sx={{fontSize: 13}}>Toddler · 2–5</MenuItem>
                <MenuItem value="7" sx={{fontSize: 13}}>Kid · 6–9</MenuItem>
                <MenuItem value="11" sx={{fontSize: 13}}>Tween · 10–13</MenuItem>
                <MenuItem value="16" sx={{fontSize: 13}}>Teen · 14–18</MenuItem>
              </Select>
            </FormControl>

            {isAdmin && (
              <>
                <Tooltip title="Delete every existing score for this video, then queue a fresh scoring pass through the gated pipeline. Progress on Admin → Jobs.">
                  <span>
                    <Button
                      size="small"
                      variant="contained"
                      color="primary"
                      disabled={rescoring || resetting || resettingSummary}
                      onClick={handleReeval}
                      sx={{textTransform: 'none'}}
                    >
                      {rescoring ? 'Queuing…' : 'Re-eval'}
                    </Button>
                  </span>
                </Tooltip>
                <Tooltip title="Delete the cached summary for this video so the next scoring pass regenerates it with the current summarizer code. Agent scores are NOT touched.">
                  <span>
                    <Button
                      size="small"
                      variant="outlined"
                      color="warning"
                      disabled={resettingSummary || resetting || rescoring}
                      onClick={handleResetSummary}
                      sx={{textTransform: 'none'}}
                    >
                      {resettingSummary ? 'Resetting…' : 'Reset summary'}
                    </Button>
                  </span>
                </Tooltip>
                <Tooltip title="Delete every existing agent score for this video (no rescore). Use this when you just want to clear the slate.">
                  <span>
                    <Button
                      size="small"
                      variant="outlined"
                      color="error"
                      disabled={resetting || rescoring || resettingSummary || agentScores.length === 0}
                      onClick={handleResetScores}
                      sx={{textTransform: 'none'}}
                    >
                      {resetting ? 'Resetting…' : 'Reset'}
                    </Button>
                  </span>
                </Tooltip>
              </>
            )}
          </Box>

          {/* =============================================================
              SCORING CHAIN: stage-by-stage visualization of how we
              arrived at this video's verdict (triage → specialists →
              final). Feedback rows are merged in as first-class
              specialist entries — see ScoringChainList for the
              override rendering.
              ============================================================= */}
          {(agentScores.length > 0 || allFeedback.length > 0) && (
            <ScoringChainPanel
              scores={agentScores}
              agents={allAgents}
              feedback={allFeedback}
              // Plumb the page's age selector down so the final-verdict
              // row in the chain renders the band-specific score / rating
              // (matching what the VerdictTile already shows). Without
              // this, switching the age filter only updated the tile but
              // left the chain showing the universal finalize verdict.
              childAge={selectedChildAge ?? undefined}
            />
          )}

          {/* =============================================================
              EVAL LAB — admin-only, side-by-side model comparison.
              Collapsed by default (runs are expensive). Each column's
              Run button fires a full pipeline eval (triage →
              specialists → finalize) with its own per-phase model
              overrides. Results live in component state; eval runs
              do NOT persist agent_scores (they'd pollute the primary
              chain view).
              ============================================================= */}
          {!pid && <EvalLabPanel videoId={vid || ''} client={client} />}

          {/* =============================================================
              TRANSCRIPT PANEL — admin-only, lazy-loaded, nicely formatted.
              Admin context is `pid === undefined` (route /admin/content/video/:vid);
              profile context has a real `pid` param. Transcripts can be
              sensitive and aren't useful to end users, so render only
              on admin.
              ============================================================= */}
          {!pid && (
            <TranscriptPanel
              videoId={vid || ''}
              client={client}
              durationSec={videoData?.duration ?? null}
            />
          )}

          {/* =============================================================
              SCORECARD RIBBON: horizontal chips, one per agent
              ============================================================= */}
          {sortedScores.length > 0 && (
            <Paper sx={{...cardSx, p: 1.5, mb: 3, overflowX: 'auto'}}>
              <Box sx={{display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'nowrap', minWidth: 0}}>
                <Typography sx={{...sectionLabelSx, pr: 1, whiteSpace: 'nowrap'}}>
                  {sortedScores.length} AGENT{sortedScores.length === 1 ? '' : 'S'}
                </Typography>
                {sortedScores.map(s => (
                  <AgentRibbonChip key={s.id} score={s} onClick={() => {
                    document.getElementById(`agent-card-${s.id}`)?.scrollIntoView({behavior: 'smooth', block: 'start'});
                  }} />
                ))}
              </Box>
            </Paper>
          )}

          {/* =============================================================
              AGENT CARDS: detail for each, sorted concerns-first
              ============================================================= */}
          {sortedScores.length === 0 && !scoresLoading ? (
            <Alert severity="info" sx={{mb: 3}}>No agent scores available for this video yet.</Alert>
          ) : (
            <Box sx={{display: 'grid', gridTemplateColumns: {xs: '1fr', md: '1fr 1fr'}, gap: 2, mb: 3}}>
              {sortedScores.map(score => (
                <AgentCard
                  key={score.id}
                  score={score}
                  isAdmin={isAdmin}
                  onViewLog={() => openLog(score)}
                  onRunTest={() => openTest(score)}
                />
              ))}
            </Box>
          )}

          {/* =============================================================
              DESCRIPTION — collapsed by default at the bottom
              ============================================================= */}
          {videoData?.description && (
            <Accordion
              sx={{
                ...cardSx,
                '&:before': {display: 'none'},
                boxShadow: 'none',
              }}
              elevation={0}
            >
              <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                <Typography sx={{fontSize: 14, fontWeight: 600}}>About this video</Typography>
              </AccordionSummary>
              <AccordionDetails>
                <Typography sx={{fontSize: 13, color: pcolors.textSecondary, whiteSpace: 'pre-wrap'}}>
                  {videoData.description}
                </Typography>
              </AccordionDetails>
            </Accordion>
          )}
        </>
      )}

      {/* ============================================================
          ADMIN: debug-log dialog
          ============================================================ */}
      <Dialog open={!!logScore} onClose={() => { setLogScore(null); setChatLog(null); }} maxWidth="md" fullWidth>
        <DialogTitle sx={{display: 'flex', alignItems: 'center', gap: 1}}>
          <BugReportIcon fontSize="small" />
          <Typography sx={{fontSize: 15, fontWeight: 600, flex: 1}}>
            {logScore ? `${humanizeSpecialization(logScore.agent.specialization)} · v${logScore.agent.version}` : 'Debug log'}
          </Typography>
        </DialogTitle>
        <DialogContent dividers>
          {chatLogLoading && (
            <Box sx={{display: 'flex', alignItems: 'center', gap: 1, py: 2}}>
              <CircularProgress size={16} />
              <Typography sx={{fontSize: 13}}>Loading chat log…</Typography>
            </Box>
          )}
          {chatLogError && <Alert severity="error">Error loading chat log: {chatLogError.message}</Alert>}
          {!isAdmin && <Alert severity="info">Debug information is only available to administrators.</Alert>}
          {isAdmin && chatLog && (
            <Box>
              <Typography sx={{fontSize: 12, color: pcolors.textSecondary, mb: 1}}>
                model: {chatLog.request.model}
                {chatLog.request.temperature != null ? ` · temp: ${chatLog.request.temperature}` : ''} · {new Date(chatLog.createdAt).toLocaleString()}
              </Typography>
              <Typography sx={sectionLabelSx}>Request messages</Typography>
              {chatLog.request.messages?.map((m: any, i: number) => (
                <Accordion key={i} defaultExpanded={i === 0} sx={{mt: 1}}>
                  <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                    <Chip size="small" label={m.role || 'unknown'} />
                  </AccordionSummary>
                  <AccordionDetails>
                    <Box sx={{position: 'relative'}}>
                      <IconButton
                        size="small"
                        sx={{position: 'absolute', top: 0, right: 0}}
                        onClick={() => navigator.clipboard.writeText(JSON.stringify(m, null, 2))}
                      >
                        <ContentCopyIcon fontSize="small" />
                      </IconButton>
                      <pre style={{margin: 0, fontSize: 11, whiteSpace: 'pre-wrap', fontFamily: 'monospace', paddingRight: 40}}>
                        {typeof m.content === 'string' ? m.content : JSON.stringify(m.content, null, 2)}
                      </pre>
                    </Box>
                  </AccordionDetails>
                </Accordion>
              ))}
              <Divider sx={{my: 2}} />
              <Typography sx={sectionLabelSx}>Response</Typography>
              <Box sx={{mt: 1, ...cardSx, p: 1.5, bgcolor: pcolors.bg}}>
                <pre style={{margin: 0, fontSize: 11, whiteSpace: 'pre-wrap', fontFamily: 'monospace'}}>
                  {JSON.stringify(chatLog.response, null, 2)}
                </pre>
              </Box>
            </Box>
          )}
          {isAdmin && !chatLogLoading && !chatLog && !chatLogError && (
            <Typography sx={{fontSize: 13, color: pcolors.textSecondary}}>No chat log available for this score.</Typography>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => { setLogScore(null); setChatLog(null); }}>Close</Button>
        </DialogActions>
      </Dialog>

      {/* ============================================================
          ADMIN: test agent scoring dialog
          ============================================================ */}
      <Dialog open={!!testScore} onClose={() => setTestScore(null)} maxWidth="md" fullWidth>
        <DialogTitle sx={{fontSize: 15, fontWeight: 600, display: 'flex', alignItems: 'center', gap: 1}}>
          <ScienceIcon fontSize="small" /> Test agent scoring
        </DialogTitle>
        <DialogContent dividers sx={{display: 'flex', flexDirection: 'column', gap: 2}}>
          {testScore && (
            <Alert severity="info" sx={{fontSize: 13}}>
              Testing <strong>{humanizeSpecialization(testScore.agent.specialization)}</strong> v{testScore.agent.version}
            </Alert>
          )}
          <TextField
            fullWidth
            multiline
            rows={3}
            label="Identity override (optional)"
            value={identityOverride}
            onChange={e => setIdentityOverride(e.target.value)}
          />
          <TextField
            fullWidth
            multiline
            rows={3}
            label="Extra guidelines (optional)"
            value={extraGuidelines}
            onChange={e => setExtraGuidelines(e.target.value)}
          />
          <Box>
            <Button variant="contained" onClick={runTest} disabled={testLoading}>
              {testLoading ? 'Testing…' : 'Run test'}
            </Button>
          </Box>
          {testError && <Alert severity="error">{testError.message}</Alert>}
          {testResult && (
            <Box sx={{...cardSx, p: 2}}>
              <Box sx={{display: 'flex', gap: 2, alignItems: 'center', mb: 1}}>
                <Chip
                  label={`Score: ${testResult.score}`}
                  size="small"
                  sx={{bgcolor: pcolors.blue[50], color: pcolors.blue[700], fontWeight: 600}}
                />
                <Chip label={`Rating: ${humanizeVideoRating(testResult.rating)}`} size="small" />
              </Box>
              <Typography sx={{fontSize: 13, whiteSpace: 'pre-wrap'}}>{testResult.explanation}</Typography>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setTestScore(null)}>Close</Button>
        </DialogActions>
      </Dialog>

      {/* FeedbackDialog removed — the multi-axis rating UI was too
          complicated for the customer surface; per-video parent
          feedback now happens via the Inbox / mobile-swipe Yes/No
          path. Component file deleted in the consolidation; if
          admin / eval needs a per-agent rating UI again, build
          it behind an admin route. */}
    </Box>
  );
}

// ---------------------------------------------------------------------------
// Verdict tile — the hero panel next to the player
// ---------------------------------------------------------------------------

function VerdictTile({
  aggregate,
  agentScores,
  loading,
}: {
  aggregate: AgentAggregateScore | null;
  agentScores: AgentScore[];
  loading: boolean;
}) {
  const harmfulCount = agentScores.filter(s => s.rating === VideoRating.Harmful).length;
  const neutralCount = agentScores.filter(s => s.rating === VideoRating.Neutral).length;
  const beneficialCount = agentScores.filter(s => s.rating === VideoRating.Beneficial).length;

  const restricted = !!aggregate?.restricted;
  const overriddenCount = aggregate?.overriddenAgents?.length ?? 0;
  // "Fully feedback-driven" means there are no agent-score rows for this
  // video AND the aggregate nonetheless came back non-null — the only
  // thing that could have produced it is a feedback row in Firestore's
  // `feedback` collection. Admins who just wiped `agent_scores` need a
  // very clear tell that the rating they're looking at isn't from an
  // agent call.
  const feedbackOnly = !!aggregate && agentScores.length === 0;
  const feedbackPartial = !feedbackOnly && overriddenCount > 0;

  const verdictColor = restricted
    ? pcolors.red[700]
    : aggregate
      ? getScoreColor(aggregate.score, ScoreDirection.HigherIsBetter)
      : pcolors.textSecondary;

  return (
    <Paper sx={{...cardSx, p: 2.5, position: {lg: 'sticky'}, top: {lg: 88}}}>
      <Typography sx={sectionLabelSx}>Verdict</Typography>
      {aggregate ? (
        <Box sx={{mt: 1, display: 'flex', alignItems: 'center', gap: 2}}>
          <Box
            sx={{
              width: 80,
              height: 80,
              borderRadius: '50%',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              bgcolor: `${verdictColor}20`,
              color: verdictColor,
              fontSize: 28,
              fontWeight: 800,
              flex: 'none',
              fontVariantNumeric: 'tabular-nums',
            }}
          >
            {restricted ? (
              <ReportGmailerrorredIcon sx={{fontSize: 40}} />
            ) : (
              aggregate.score.toFixed(1)
            )}
          </Box>
          <Box sx={{minWidth: 0}}>
            <Typography sx={{fontSize: 16, fontWeight: 700, color: pcolors.textPrimary}}>
              {restricted ? 'Restricted' : humanizeVideoRating(aggregate.rating)}
            </Typography>
            <Typography sx={{fontSize: 11, color: pcolors.textSecondary, mt: 0.25}}>
              {agentScores.length} agent{agentScores.length === 1 ? '' : 's'} · {beneficialCount} beneficial · {neutralCount} neutral · {harmfulCount} harmful
            </Typography>
            {/* Feedback-driven callout. Placed right under the counts so
                an admin who just reset scores immediately sees why the
                video still shows a verdict. */}
            {(feedbackOnly || feedbackPartial) && (
              <Tooltip
                title={
                  feedbackOnly
                    ? 'No agent scores exist for this video — the verdict is derived entirely from user/admin feedback stored in Firestore. Resetting agent scores does not clear feedback; use the feedback list to remove it.'
                    : `${overriddenCount} agent score${overriddenCount === 1 ? '' : 's'} overridden by user/admin feedback. The feedback wins over the agent for those specializations.`
                }
              >
                <Chip
                  size="small"
                  label={
                    feedbackOnly
                      ? 'Feedback-driven (no agent scores)'
                      : `Feedback override · ${overriddenCount}`
                  }
                  sx={{
                    mt: 0.75,
                    fontSize: 10,
                    fontWeight: 700,
                    bgcolor: pcolors.amber[50],
                    color: pcolors.amber[700],
                    textTransform: 'uppercase',
                    letterSpacing: 0.3,
                  }}
                />
              </Tooltip>
            )}
          </Box>
        </Box>
      ) : loading ? (
        <Box sx={{mt: 1, display: 'flex', alignItems: 'center', gap: 1, color: pcolors.textMuted}}>
          <CircularProgress size={16} />
          <Typography sx={{fontSize: 13}}>Loading verdict…</Typography>
        </Box>
      ) : (
        <Box sx={{mt: 1}}>
          <Typography sx={{fontSize: 14, fontWeight: 600, color: pcolors.textPrimary}}>
            Not yet reviewed
          </Typography>
          <Typography sx={{fontSize: 12, color: pcolors.textSecondary, mt: 0.5, lineHeight: 1.5}}>
            No agent has scored this video yet. Use “Rate this video” below to
            add feedback, or trigger a re-score from the admin controls.
          </Typography>
        </Box>
      )}

      {aggregate?.explanation && (
        <Typography sx={{fontSize: 12, color: pcolors.textSecondary, mt: 1.5, lineHeight: 1.55}}>
          {aggregate.explanation}
        </Typography>
      )}
    </Paper>
  );
}

// ---------------------------------------------------------------------------
// Scorecard ribbon chip
// ---------------------------------------------------------------------------

function AgentRibbonChip({score, onClick}: {score: AgentScore; onClick: () => void}) {
  const bg = getScoreColor(score.score, score.scoreDirection);
  return (
    <Tooltip title={humanizeSpecialization(score.agent.specialization)}>
      <Box
        onClick={onClick}
        sx={{
          display: 'inline-flex',
          alignItems: 'center',
          gap: 0.5,
          pl: 0.5,
          pr: 1,
          py: 0.5,
          borderRadius: 999,
          bgcolor: pcolors.card,
          border: `1px solid ${pcolors.border}`,
          cursor: 'pointer',
          transition: 'all 0.15s',
          '&:hover': {borderColor: bg, transform: 'translateY(-1px)'},
        }}
      >
        <Box
          sx={{
            width: 22,
            height: 22,
            borderRadius: '50%',
            bgcolor: bg,
            color: '#fff',
            fontSize: 11,
            fontWeight: 700,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            fontVariantNumeric: 'tabular-nums',
          }}
        >
          {score.score.toFixed(0)}
        </Box>
        <Typography sx={{fontSize: 11, color: pcolors.textPrimary, fontWeight: 500, whiteSpace: 'nowrap'}}>
          {humanizeSpecialization(score.agent.specialization)}
        </Typography>
        {ratingIcon(score.rating, 14)}
      </Box>
    </Tooltip>
  );
}

// ---------------------------------------------------------------------------
// Agent detail card
// ---------------------------------------------------------------------------

function AgentCard({
  score,
  isAdmin,
  onViewLog,
  onRunTest,
}: {
  score: AgentScore;
  isAdmin: boolean;
  onViewLog: () => void;
  onRunTest: () => void;
}) {
  const scoreBg = getScoreColor(score.score, score.scoreDirection);

  return (
    <Paper id={`agent-card-${score.id}`} sx={{...cardSx, p: 2}}>
      <Box sx={{display: 'flex', alignItems: 'flex-start', gap: 1.5}}>
        <Box sx={{flex: 1, minWidth: 0}}>
          <Typography sx={{fontSize: 14, fontWeight: 700, color: pcolors.textPrimary, lineHeight: 1.3}}>
            {humanizeSpecialization(score.agent.specialization)}
          </Typography>
          <Typography sx={{fontSize: 11, color: pcolors.textMuted, fontFamily: 'monospace', mt: 0.25}}>
            v{score.agent.version} · {score.type.toString().toLowerCase()}
          </Typography>
        </Box>
        <Box sx={{display: 'flex', alignItems: 'center', gap: 0.75}}>
          {isAdmin && (
            <>
              <Tooltip title="View debug log">
                <IconButton size="small" onClick={onViewLog}>
                  <BugReportIcon sx={{fontSize: 16}} />
                </IconButton>
              </Tooltip>
              <Tooltip title="Test this agent">
                <IconButton size="small" onClick={onRunTest}>
                  <ScienceIcon sx={{fontSize: 16}} />
                </IconButton>
              </Tooltip>
            </>
          )}
          <Box
            sx={{
              px: 1.25,
              py: 0.25,
              borderRadius: 1.5,
              bgcolor: scoreBg,
              color: '#fff',
              fontSize: 13,
              fontWeight: 700,
              fontVariantNumeric: 'tabular-nums',
              minWidth: 34,
              textAlign: 'center',
            }}
          >
            {score.score.toFixed(1)}
          </Box>
          <Box
            sx={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: 0.4,
              px: 0.75,
              py: 0.25,
              borderRadius: 1,
              border: `1px solid ${pcolors.border}`,
              fontSize: 11,
            }}
          >
            {ratingIcon(score.rating, 14)}
            <Typography sx={{fontSize: 11, color: pcolors.textSecondary}}>
              {humanizeVideoRating(score.rating)}
            </Typography>
          </Box>
        </Box>
      </Box>

      {score.explanation && (
        <Typography sx={{mt: 1.25, fontSize: 13, color: pcolors.textPrimary, lineHeight: 1.6, whiteSpace: 'pre-wrap'}}>
          {score.explanation}
        </Typography>
      )}
    </Paper>
  );
}
